import { configFrom, formatMass, loadFor, plans, startSession, timingFrom, type Gym, type Prescribed, type ProgrammeState, type RepwiseEvent } from '@repwise/domain';
import { uuidv7 } from '@repwise/sync';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadIndex } from '../lib/configs.ts';
import { NextDate } from './NextDate.tsx';
import { PlateStrip, WeightButton } from './PlateSheet.tsx';
import { wt } from '../lib/copy.ts';
import { LIFT_NAME } from '../lib/defaults.ts';
import { useRepo, useSyncStatus } from '../lib/hooks.ts';
import type { useHostedPlan } from '../lib/plan.ts';
import { Button, Card, SyncPill, TabHeader } from './ui.tsx';
import { WeightEdit } from './WeightEdit.tsx';

type Hosted = ReturnType<typeof useHostedPlan>;



/** The next sessions if today succeeds (J19 Upcoming), from the plan engine. */
function upcoming(hosted: Hosted, unit: 'kg' | 'lb', gym: Gym, count = 2) {
  const plan = hosted.plan!;
  let st = hosted.live!.state;
  let today = hosted.live!.next;
  const out: { workout: string; prescriptions: Prescribed[] }[] = [];
  for (let i = 0; i < count; i++) {
    st = plans.finishSession(plan, st, unit, today.prescriptions.map((p) => (p.setPlan ?? []).map((x) => x.reps)), today.workout);
    // only "from now on" swaps reach past the next workout
    today = plans.planPrescriptions(plan, st, unit, gym, hosted.catalogue ?? undefined, undefined, Object.fromEntries(Object.entries(hosted.live!.swaps).filter(([, s]) => s.scope === 'ongoing')));
    out.push({ workout: today.workout, prescriptions: today.prescriptions });
  }
  return out;
}

/** Plain words for a set plan (brief §3): "1 heavy set of 5 at 100 kg, then 4 × 5 at 90 kg". */
export function describeSets(p: Prescribed, unit: string): string {
  const sp = p.setPlan ?? [];
  const w = (m: number | null) => (m === null ? 'bodyweight' : wt(m, unit as 'kg'));
  if (!sp.length) return `${p.sets} × ${p.reps} at ${w(p.weight)}`;
  if (p.bodyweight) return `${sp.length} × ${sp[0]!.reps}`;
  const ramps = sp.filter((s) => s.kind === 'ramp');
  const top = sp.find((s) => s.kind === 'top');
  const backs = sp.filter((s) => s.kind === 'backoff');
  if (ramps.length && top) {
    const after = backs.length ? `, then ${backs.length} × ${backs[0]!.reps} at ${w(backs[0]!.weight)}` : '';
    return `${ramps.length} sets building up, then ${top.reps} at ${w(top.weight)}${after}`;
  }
  if (top && backs.length) return `1 heavy set of ${top.reps} at ${w(top.weight)}, then ${backs.length} × ${backs[0]!.reps} at ${w(backs[0]!.weight)}`;
  const same = sp.every((s) => s.weight === sp[0]!.weight && s.reps === sp[0]!.reps);
  return same ? `${sp.length} × ${sp[0]!.reps} at ${w(sp[0]!.weight)}` : sp.map((s) => `${s.reps} at ${w(s.weight)}`).join(', ');
}

/** S01 for a hosted plan (J17), driven by the programme lifecycle (ADR-0021). */
export function PlanReady({ programme, events, hosted }: { programme: ProgrammeState; events: RepwiseEvent[]; hosted: Hosted }) {
  const { store } = useRepo();
  const status = useSyncStatus();
  // BL01 (J25): "Keep going" is an event, so the card stays dismissed on every device and after a reinstall.
  const keepGoing = () => void store.append([{ type: 'block_continued', id: uuidv7(), at: Date.now(), selectionId: hosted.selection!.id }]);
  const [titles, setTitles] = useState<Record<string, { title: string; summary?: string; daysPerWeek?: number }>>({});
  const blockDone = hosted.mode?.kind === 'hosted' && hosted.mode.status === 'block-complete';
  const needsDownload = hosted.mode?.kind === 'hosted' && hosted.mode.status === 'needs-download';
  useEffect(() => {
    if (!blockDone && !needsDownload) return;
    let alive = true;
    void loadIndex(store).then((r) => alive && setTitles(Object.fromEntries((r.index?.plans ?? []).map((e) => [e.id, { title: e.title, summary: e.summary, daysPerWeek: e.daysPerWeek }]))));
    return () => {
      alive = false;
    };
  }, [blockDone, needsDownload, store]);
  const unit = hosted.selection?.unit ?? programme.settings.unit;
  const [swapping, setSwapping] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);
  const mode = hosted.mode;

  if (!mode || mode.kind !== 'hosted') return null;
  if (mode.status === 'needs-download')
    return (
      <main className="p-5 pb-40 max-w-[480px] mx-auto">
        <TabHeader title="Your plan">
          <SyncPill status={status} />
        </TabHeader>
        {titles[hosted.selection!.planId!] && <p className="text-text-muted">{titles[hosted.selection!.planId!]!.title}</p>}
        <Card className="mt-5">
          <h2 className="text-headline">Download your plan to start</h2>
          <p className="mt-1">Your programme was chosen on another device and isn't on this one yet. Download it once; after that it works offline.</p>
          <div className="mt-3">
            <Button disabled={hosted.busy} onClick={() => void hosted.download()}>
              {hosted.busy ? 'Downloading…' : 'Download my plan'}
            </Button>
          </div>
          {hosted.error ? (
            <p className="text-miss text-sm mt-2" role="alert">
              {hosted.error === 'offline' ? 'You need a connection to download it. Your history is safe.' : "This plan couldn't be loaded. Try again, or choose another in Settings."}
            </p>
          ) : (
            <p className="text-xs text-text-muted mt-2">Offline? You need a connection to download it. Your history is safe.</p>
          )}
        </Card>
      </main>
    );

  const plan = hosted.plan!;
  const live = hosted.live!;
  const workout = plan.workouts.find((w) => w.id === live.next.workout)!;
  const start = (other?: string) => {
    const kind = other ?? live.next.workout;
    const prescriptions = other ? plans.planPrescriptions(plan, live.state, unit as 'kg' | 'lb', programme.settings.gym, hosted.catalogue ?? undefined, other, live.swaps).prescriptions : live.next.prescriptions;
    const { events } = startSession({
      id: uuidv7(),
      workoutId: uuidv7(),
      kind,
      at: Date.now(),
      prescriptions,
      config: configFrom(programme.settings),
      timing: timingFrom(programme.settings),
      plan: { id: plan.id, version: plan.version, workout: kind },
    });
    void store.append(events);
  };

  return (
    <main className="p-5 pb-40 max-w-[480px] mx-auto">
      <TabHeader title={`Workout ${live.next.workout}`}>
        <SyncPill status={status} />
      </TabHeader>
      <p className="text-text-muted">
        {workout.name} · {plan.title} · week {Math.min(mode.week, plan.blockWeeks)}
        {hosted.selection?.variation ? ` · ${plan.variations?.find((v) => v.id === hosted.selection!.variation)?.label ?? ''}` : ''}
        {mode.week > plan.blockWeeks ? '+' : ''}
      </p>
      <NextDate events={events} daysPerWeek={plan.daysPerWeek} workout={live.next.workout} />

      {mode.status === 'block-complete' && !mode.continued && (
        <Card className="mt-5">
          <h2 className="text-headline"><span aria-hidden="true">🏆 </span>You've finished {plan.blockWeeks} weeks of {plan.title}</h2>
          <p className="text-sm text-text-muted mt-1">Nothing changes unless you choose.</p>
          <div className="mt-3 space-y-2">
            <Button onClick={keepGoing}>Keep going with the same rules</Button>
            <Link to={`/settings/programme/${plan.id}/start`} className="block">
              <Button kind="secondary">New block of {plan.title}</Button>
            </Link>
            <p className="text-xs text-text-muted">A new block starts from your latest successful sets, with a fresh on-ramp.</p>
          </div>
          {(plan.next ?? []).length > 0 && (
            <div className="mt-4">
              {/* BL01 `_next_programme_list` final (Wave 2.4 batch 2): the plan's own suggestions, from its file */}
              <h3 className="text-headline">Or a next programme</h3>
              <ul className="mt-2 space-y-2">
                {plan.next!.map((id) => (
                  <li key={id} className="bg-surface-sunken rounded-[14px] p-3">
                    <h4 className="text-callout">{titles[id]?.title ?? id}</h4>
                    {titles[id]?.daysPerWeek && <span className="inline-block mt-1 text-xs rounded-full bg-surface-raised px-2 py-0.5">{titles[id]!.daysPerWeek} days a week</span>}
                    {titles[id]?.summary && <p className="text-sm text-text-muted mt-1">{titles[id]!.summary}</p>}
                    <Link to={`/settings/programme/${id}`} className="mt-2 flex items-center justify-center rounded-[12px] bg-surface-raised min-h-11 text-sm">
                      See {titles[id]?.title ?? id}
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-text-muted mt-2">Each starts from your latest sets: you'll see where every lift starts before anything changes.</p>
            </div>
          )}
        </Card>
      )}

      {/* S01b `_ultra_4day` final (Wave 2.4 batch 1): role, name and weight; the sets in words; the bar per side */}
      <div className="flex justify-between items-baseline mt-5">
        <h2 className="text-headline">Today</h2>
        <span className="text-xs text-text-muted">
          {live.next.prescriptions.length} exercise{live.next.prescriptions.length === 1 ? '' : 's'}
        </span>
      </div>
      <ul className="mt-2 space-y-3">
        {live.next.prescriptions.map((p) => {
          const adjusted = p.setPlan?.some((s) => s.target !== undefined);
          const top = p.setPlan?.find((s) => s.kind === 'top') ?? p.setPlan?.at(-1);
          // Ultra's tracks are "Squat (Main)" / "Squat (Secondary)": show the role above the name
          const role = /\((Main|Secondary)\)$/.exec(plan.tracks.find((t) => t.id === p.track)?.label ?? '')?.[1];
          const side = top && top.weight !== null && p.barbell ? loadFor(programme.settings.gym, top.weight) : null;
          return (
            <li key={p.exercise}>
              <Card>
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    {role && <span className={`block text-xs ${role === 'Main' ? 'text-now-ink' : 'text-text-muted'}`}>{role}</span>}
                    <h2 className="text-headline">{p.name ?? LIFT_NAME[p.exercise]}</h2>
                    {top?.target !== undefined && <span className="inline-block mt-1 text-xs rounded-full bg-surface-sunken px-2 py-1">Nearest you can load</span>}
                  </div>
                  {top && top.weight !== null ? (
                    <span className="bg-surface-sunken rounded-[10px] px-3 py-1 shrink-0">
                      <WeightButton gym={programme.settings.gym} weight={top.weight}><span className="num text-num-l">{formatMass(top.weight)}</span></WeightButton> <span className="text-xs text-text-muted">{unit}</span>
                    </span>
                  ) : (
                    p.weight === null && <span className="bg-surface-sunken rounded-[10px] px-3 py-1 text-sm shrink-0">Bodyweight</span>
                  )}
                </div>
                {p.swappedFrom && p.track && (
                  <p className="text-xs text-text-muted">
                    Instead of {LIFT_NAME[p.swappedFrom]} · {live.swaps[p.track]?.scope === 'next' ? 'just this workout' : 'from now on'}{' '}
                    <button type="button" className="text-now-ink underline min-h-11 px-1" onClick={() => void store.append([{ type: 'exercise_swapped', id: uuidv7(), at: Date.now(), selectionId: hosted.selection!.id, track: p.track!, to: p.swappedFrom!, scope: live.swaps[p.track!]?.scope ?? 'ongoing' }])}>
                      Undo swap
                    </button>
                  </p>
                )}
                <p className="mt-1">{describeSets(p, unit)}</p>
                {side && top && (
                  <div className="mt-2 bg-surface-sunken rounded-[12px] p-2 space-y-1">
                    <p className="flex justify-between gap-2 text-xs text-text-muted">
                      <span>
                        Each side: {side.map(formatMass).join(' + ') || 'just the bar'} {unit}
                        {top.target !== undefined ? ` · plan ${formatMass(top.target)}` : ''}
                      </span>
                      {side.length > 0 && (
                        <span className="text-text font-medium shrink-0">
                          {formatMass(side.reduce((x, y) => x + y, 0))} {unit} / side
                        </span>
                      )}
                    </p>
                    <PlateStrip gym={programme.settings.gym} weight={top.weight!} />
                  </div>
                )}
                {adjusted && top?.target === undefined && <p className="text-xs text-text-muted mt-1">Some sets load the nearest weight your plates can make.</p>}
                {/* FB-002 on plans (ADR-0025): change this lift's top set; the plan's rules carry on from it */}
                {top && top.weight !== null && p.track && !p.swappedFrom && !programme.inProgress.length && (
                  <WeightEdit name={p.name ?? LIFT_NAME[p.exercise] ?? p.exercise} weight={top.weight} unit={unit as 'kg' | 'lb'} min={p.barbell ? programme.settings.gym.bar : 100} onSave={(to) => void store.append([{ type: 'plan_weight_set', id: uuidv7(), at: Date.now(), selectionId: hosted.selection!.id, track: p.track!, from: top.target ?? top.weight!, to }])} />
                )}
                {hosted.catalogue && p.track && !programme.inProgress.length && (
                  <button type="button" className="text-now-ink text-sm underline min-h-11 px-1" aria-label={`Swap ${p.name ?? LIFT_NAME[p.exercise]}`} onClick={() => setSwapping(p.track!)}>
                    Swap
                  </button>
                )}
                {swapping === p.track && hosted.catalogue && (
                  <SwapSheet p={p} unit={unit} catalogue={hosted.catalogue} onCancel={() => setSwapping(null)} onSwap={(to, scope, ratio) => (setSwapping(null), void store.append([{ type: 'exercise_swapped', id: uuidv7(), at: Date.now(), selectionId: hosted.selection!.id, track: p.track!, to, scope, ...(ratio !== 1 ? { ratio } : {}) }]))} />
                )}
              </Card>
            </li>
          );
        })}
      </ul>

      <div className="flex justify-between items-center mt-4">
        <Link to="/settings/programme" className="text-now-ink text-sm min-h-11 inline-flex items-center">
          Programme ›
        </Link>
        {plan.rotation.length > 1 && (
          <button type="button" className="text-text-muted underline min-h-11 px-1" aria-expanded={choosing} onClick={() => setChoosing(true)}>
            Do another workout instead
          </button>
        )}
      </div>
      {choosing && (
        <RotationSheet
          plan={plan}
          current={live.next.workout}
          unit={unit}
          preview={(k) => (k === live.next.workout ? live.next.prescriptions : plans.planPrescriptions(plan, live.state, unit as 'kg' | 'lb', programme.settings.gym, hosted.catalogue ?? undefined, k, live.swaps).prescriptions)}
          onClose={() => setChoosing(false)}
          // picking one makes it today's workout at once (the rotation carries on from it), as the chips did
          onPick={(k) => k !== live.next.workout && void store.append([{ type: 'next_workout_set', id: uuidv7(), at: Date.now(), kind: k }])}
          onStart={(k) => (setChoosing(false), start(k === live.next.workout ? undefined : k))}
        />
      )}

      <section className="mt-6" aria-label="Upcoming">
        <div className="flex justify-between items-baseline">
          <h2 className="text-headline">Upcoming</h2>
          <span className="text-xs text-text-muted">If today succeeds</span>
        </div>
        <ul className="mt-2 space-y-2">
          {upcoming(hosted, unit as 'kg' | 'lb', programme.settings.gym).map((u, i) => {
            const todayTop = (ex: string) => live.next.prescriptions.find((p) => p.exercise === ex)?.weight ?? null;
            return (
              <li key={i}>
                <Card>
                  <h3 className="text-callout">
                    Workout {u.workout} · {plan.workouts.find((w) => w.id === u.workout)?.name}
                  </h3>
                  <ul className="mt-1 text-sm space-y-1">
                    {u.prescriptions.map((p) => {
                      const up = p.weight !== null && todayTop(p.exercise) !== null && p.weight > todayTop(p.exercise)!;
                      return (
                        <li key={p.exercise} className="flex justify-between gap-3 bg-surface-sunken rounded-[8px] px-2 py-1">
                          <span className="shrink-0">{p.name ?? LIFT_NAME[p.exercise]}</span>
                          <span className="text-right text-text-muted">
                            {up && <span className="text-now-ink" aria-label="goes up">↑ </span>}
                            {describeSets(p, unit)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </Card>
              </li>
            );
          })}
        </ul>
      </section>

      <div className="fixed inset-x-0 p-5 bg-surface" style={{ bottom: 'var(--tabbar-h, calc(4rem + env(safe-area-inset-bottom)))' }}>
        <div className="max-w-[480px] mx-auto">
          <Button onClick={() => start()}>Start workout {live.next.workout}</Button>
        </div>
      </div>
    </main>
  );
}

/** S01b `_rotation_open` (Wave 2.3 final): pick today's workout from the rotation, see its sets, start it. */
function RotationSheet({ plan, current, unit, preview, onClose, onPick, onStart }: { plan: plans.PlanConfig; current: string; unit: string; preview: (k: string) => Prescribed[]; onClose: () => void; onPick: (k: string) => void; onStart: (k: string) => void }) {
  const [pick, setPick] = useState(current);
  const sets = preview(pick);
  const name = (k: string) => plan.workouts.find((w) => w.id === k)?.name;
  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end" role="dialog" aria-modal="true" aria-label="Do another workout instead">
      <button type="button" className="absolute inset-0 bg-surface-sunken/80" aria-label="Close" onClick={onClose} />
      <div className="relative bg-surface-overlay rounded-t-[20px] p-5 space-y-4 max-h-[85vh] overflow-y-auto max-w-[480px] w-full mx-auto" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)' }}>
        <div className="flex justify-between items-center gap-3">
          <h2 className="text-headline">Do another workout instead</h2>
          <button type="button" className="min-w-11 min-h-11 rounded-full bg-surface-sunken shrink-0" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        <p className="text-sm text-text-muted">Pick today's workout. {plan.title} carries on from the one you do.</p>
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(plan.rotation.length, 3)}, minmax(0, 1fr))` }} role="radiogroup" aria-label="Choose today's workout">
          {plan.rotation.map((k) => (
            <button key={k} type="button" role="radio" aria-checked={pick === k} aria-label={`Workout ${k} (${name(k)})`} className={`text-left rounded-[12px] p-3 min-h-11 ${pick === k ? 'bg-surface-raised ring-2 ring-now-ink' : 'bg-surface-sunken'}`} onClick={() => (setPick(k), onPick(k))}>
              <span className="block text-headline">{k}</span>
              <span className="block text-sm text-text-muted">{name(k)}</span>
              {k === pick && <span className="block text-xs text-now-ink mt-1">Today</span>}
            </button>
          ))}
        </div>
        <section className="bg-surface-sunken rounded-[14px] p-3 space-y-2" aria-label={`Workout ${pick}`}>
          <div className="flex justify-between items-baseline">
            <h3 className="text-callout">Workout {pick}</h3>
            <span className="text-xs text-text-muted">
              {sets.length} lift{sets.length === 1 ? '' : 's'} · {sets.reduce((n, p) => n + (p.setPlan?.length ?? p.sets), 0)} sets
            </span>
          </div>
          <ul className="space-y-2">
            {sets.map((p) => {
              const top = p.setPlan?.find((x) => x.kind === 'top') ?? p.setPlan?.at(-1);
              const w = top ? top.weight : p.weight;
              return (
                <li key={p.exercise} className="flex justify-between items-center gap-3 bg-surface-raised rounded-[10px] px-3 py-2">
                  <span>
                    <span className="block">{p.name ?? LIFT_NAME[p.exercise]}</span>
                    <span className="block text-xs text-text-muted">{describeSets(p, unit)}</span>
                  </span>
                  <span className="num text-num-l shrink-0">{w === null ? 'Bodyweight' : wt(w, unit as 'kg')}</span>
                </li>
              );
            })}
          </ul>
        </section>
        <Button onClick={() => onStart(pick)}>Start workout {pick}</Button>
      </div>
    </div>
  );
}

/**
 * SW01 (J18): another exercise from the same family in this slot. It keeps the slot's progression. Different
 * equipment (barbell → dumbbells) asks for a starting weight; the plan's weights for the slot are scaled to it.
 */
export function SwapSheet({ p, unit, catalogue, onSwap, onCancel }: { p: Prescribed; unit: 'kg' | 'lb'; catalogue: plans.ExerciseCatalogue; onSwap: (to: string, scope: 'next' | 'ongoing', ratio: number) => void; onCancel: () => void }) {
  const own = p.swappedFrom ?? p.exercise;
  const ownCat = catalogue.exercises.find((e) => e.id === own);
  const options = catalogue.exercises.filter((e) => e.family === ownCat?.family && e.id !== p.exercise);
  const [to, setTo] = useState<string | null>(null);
  const [scope, setScope] = useState<'next' | 'ongoing'>('next');
  const top = p.weight !== null ? p.weight / 100 : null;
  const [picked, setW] = useState<number | null>(null);
  const choice = catalogue.exercises.find((e) => e.id === to);
  const kind = (e?: { equipment: string[]; bodyweight?: boolean }) => (e?.bodyweight ? 'bodyweight' : e?.equipment.includes('barbell') ? 'barbell' : e?.equipment.includes('dumbbells') ? 'dumbbells' : 'machine');
  const needsWeight = !!choice && !choice.bodyweight && top !== null && kind(choice) !== kind(ownCat);
  // Barbell → dumbbells starts each hand at about a third of the barbell top set (owner, 14 Oct 2026; commonly
  // quoted as 35–40%), on the stepper's 2.5 kg / 5 lb steps. The user then moves it to the dumbbells they have.
  const big = unit === 'kg' ? 2.5 : 5;
  const suggested = top === null ? null : choice && kind(choice) === 'dumbbells' && kind(ownCat) === 'barbell' ? Math.max(big, Math.round(top / 3 / big) * big) : top;
  const w = picked ?? suggested;
  const step = unit === 'kg' ? 0.5 : 1;
  return (
    <div className="mt-3 bg-surface-sunken rounded-[12px] p-3 space-y-3" role="group" aria-label={`Swap ${p.name ?? LIFT_NAME[p.exercise]}`}>
      <p className="text-sm">Do another {(catalogue.families[ownCat?.family ?? ''] ?? 'exercise').toLowerCase()} exercise in this slot. It keeps this slot's progression.</p>
      <p className="flex justify-between text-xs text-text-muted">
        <span>Exercises in this family</span>
        <span>{options.filter((e) => e.id !== own).length + (p.swappedFrom && ownCat ? 1 : 0)} available</span>
      </p>
      <ul className="space-y-1" aria-label="Substitutes">
        {to !== own && ownCat && p.swappedFrom && (
          <li>
            <button type="button" role="radio" aria-checked={to === own} className="w-full text-left min-h-11 px-3 rounded-[10px] border border-outline" onClick={() => (setTo(own), setW(null))}>
              {ownCat.name} <span className="text-xs text-text-muted">(the plan's own)</span>
            </button>
          </li>
        )}
        {options.filter((e) => e.id !== own).map((e) => (
          <li key={e.id}>
            <button type="button" role="radio" aria-checked={to === e.id} className={`w-full text-left min-h-11 px-3 rounded-[10px] border ${to === e.id ? 'border-now-ink text-now-ink' : 'border-outline'}`} onClick={() => (setTo(e.id), setW(null))}>
              {e.name} <span className="text-xs text-text-muted">· {e.bodyweight ? 'bodyweight' : e.equipment.map((x) => x.replace(/-/g, ' ')).join(', ')}</span>
            </button>
          </li>
        ))}
      </ul>
      {to && (
        <fieldset>
          <legend className="text-sm">For how long?</legend>
          <div className="flex gap-2 mt-1" role="radiogroup" aria-label="For how long">
            {(['next', 'ongoing'] as const).map((s) => (
              <button key={s} type="button" role="radio" aria-checked={scope === s} className={`flex-1 min-h-11 rounded-[10px] border text-sm ${scope === s ? 'border-now-ink text-now-ink' : 'border-outline'}`} onClick={() => setScope(s)}>
                {s === 'next' ? 'Just this workout' : 'From now on'}
              </button>
            ))}
          </div>
        </fieldset>
      )}
      {needsWeight && w !== null && (
        <label className="block text-sm">
          Top-set weight for {choice!.name}
          {kind(choice) === 'dumbbells' ? ' (each hand)' : ''}
          <span className="flex items-center gap-2 mt-1">
            <button type="button" className="min-w-11 min-h-11 rounded-full border border-outline" aria-label="Lower swap weight" onClick={() => setW(Math.max(step, +((w ?? 0) - step * 5).toFixed(2)))}>
              −
            </button>
            <span className="num text-num-l">
              {w} {unit}
            </span>
            <button type="button" className="min-w-11 min-h-11 rounded-full border border-outline" aria-label="Raise swap weight" onClick={() => setW(+((w ?? 0) + step * 5).toFixed(2))}>
              +
            </button>
          </span>
          <span className="block text-xs text-text-muted mt-1">The plan's top set here is {top} {unit} on the {kind(ownCat)}. Other sets scale with it.</span>
        </label>
      )}
      <div className="space-y-1">
        {/* SW01 final (Wave 2.4 batch 2): say what each button does */}
        <Button disabled={!to} onClick={() => to && onSwap(to, scope, needsWeight && w && top ? +(w / top).toFixed(4) : 1)} label="Swap">
          {choice ? `Swap to ${choice.name}` : 'Swap'}
        </Button>
        <Button kind="text" onClick={onCancel}>
          Keep {p.name ?? LIFT_NAME[p.exercise]}
        </Button>
      </div>
    </div>
  );
}
